import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {GeoCoord} from "./geo-coord";
import {Entity, Locatable} from "./entity";
import {DatabaseCode, toDatabaseCode} from "./database-code";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {PersonStateEntity, PersonStateValidator} from "./person-state";
import {validateJSON, validateJSONNow} from "../web/validation";
import {toLanguage} from "./language";
import {RoleArrayValidator, RoleEntity} from "./role";

/** Validator for Shift JSON */
export const PersonValidator = t.intersection([
  t.type({
    id: t.string,
    firstName: t.string,
    lastName: t.string,
    type: t.string,
    companyId: t.string,
    doNotUse: t.boolean
  }),
  t.partial({
    roles: RoleArrayValidator,
    active: t.boolean,
    userName: t.string,
    password: t.string,
    passwordSet: t.boolean,
    passwordChanged: t.boolean,
    shiftTypeId: t.string,
    shiftTypeName: t.string,
    /** Day totals the server fills in on driver activity person rows. */
    shiftCount: t.number,
    totalLoads: t.number,
    totalTime: t.number,
    totalQty: t.number,
    totalCost: t.number,
    totalPersonCost: t.number,
    totalTrailerCost: t.number,
    barcode: t.string,
    phoneNumber: t.string,
    emailAddress: t.string,
    preferredLanguage: t.string,
    regionList: t.array(t.string),
    companyName: t.string,
    companyType: t.string,
    state: PersonStateValidator,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    database: t.string
  })
]);

/** Validator for an array of persons. */
export const PersonArrayValidator = t.array(PersonValidator);

export type Person = t.TypeOf<typeof PersonValidator>;
export type PersonArray = t.TypeOf<typeof PersonArrayValidator>;

type PersonTypeCategory = "FIELD";

/**
 * The type of person.
 */
export type PersonType = DatabaseCode & {
  categories: PersonTypeCategory[];
}

const fieldTech: PersonType = {
  code: "FIELDTECHNICIAN",
  displayValue: {
    id: "person.type.fieldtech",
    defaultMessage: "Field Tech"
  },
  categories: ["FIELD"]
};

const wellAttendant: PersonType = {
  code: "WELLATTENDANT",
  displayValue: {
    id: "person.type.wellattendant",
    defaultMessage: "Well Attendant"
  },
  categories: ["FIELD"]
};

const driver: PersonType = {
  code: "DRIVER",
  displayValue: {
    id: "person.type.driver",
    defaultMessage: "Driver"
  },
  categories: []
};

const user: PersonType = {
  code: "USER",
  displayValue: {
    id: "person.type.user",
    defaultMessage: "User"
  },
  categories: ["FIELD"]
};

const restricted: PersonType = {
  code: "RESTRICTED",
  displayValue: {
    id: "person.type.restricted",
    defaultMessage: "Restricted"
  },
  categories: ["FIELD"]
};

const admin: PersonType = {
  code: "ADMIN",
  displayValue: {
    id: "person.type.admin",
    defaultMessage: "Admin"
  },
  categories: []
};

const pumper: PersonType = {
  code: "PUMPER",
  displayValue: {
    id: "person.type.pumper",
    defaultMessage: "Pumper"
  },
  categories: ["FIELD"]
};

const waterTransfer: PersonType = {
  code: "WATERTRANSFER",
  displayValue: {
    id: "person.type.watertransfer",
    defaultMessage: "Water Trans."
  },
  categories: ["FIELD"]
};

const tablet: PersonType = {
  code: "TABLET",
  displayValue: {
    id: "person.type.tablet",
    defaultMessage: "App Identity"
  },
  categories: []
};

/**
 * Types for {@link Person}.
 */
export const personType = {
  fieldTech,
  wellAttendant,
  driver,
  user,
  restricted,
  admin,
  pumper,
  waterTransfer,
  tablet
};

export const personTypes = Object.values(personType);

export const toPersonType = (code: string): PersonType => toDatabaseCode(code, personTypes);

/**
 * A person.
 */
@Exclude()
export class PersonEntity implements Entity, Locatable {
  @Expose()
  id!: string;

  @Expose()
  firstName!: string;

  @Expose()
  lastName!: string;

  @Expose()
  emailAddress?: string;

  @Transform(getDatabaseCodeTransformer(toLanguage))
  @Expose()
  preferredLanguage?: DatabaseCode

  @Expose()
  password?: string;

  @Expose()
  passwordSet?: boolean;

  @Expose()
  passwordChanged?: boolean;

  @Expose()
  shiftTypeId?: string;

  @Expose()
  readonly shiftTypeName?: string;

  @Expose()
  readonly shiftCount?: number;

  @Expose()
  readonly totalLoads?: number;

  @Expose()
  readonly totalTime?: number;

  @Expose()
  readonly totalQty?: number;

  @Expose()
  readonly totalCost?: number;

  @Expose()
  readonly totalPersonCost?: number;

  @Expose()
  readonly totalTrailerCost?: number;

  @Expose()
  phoneNumber?: string

  @Expose()
  barcode?: string

  @Expose()
  userName?: string

  @Transform(getDatabaseCodeTransformer(toPersonType, restricted))
  @Expose()
  type!: PersonType;

  @Expose()
  companyId!: string;

  @Expose()
  companyName?: string;

  @Expose()
  companyType?: string;

  @Expose()
  @Type(() => PersonStateEntity)
  state?: PersonStateEntity;

  @Expose()
  doNotUse = false;

  @Expose()
  active = true;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose({name: "regionList"})
  regionIds?: string[];

  @Expose()
  @Type(() => RoleEntity)
  roles?: RoleEntity[];

  @Expose()
  readonly database?: string;

  readonly entityType = "PERSON";

  get displayName(): Readonly<string> {
    let name = this.nameReverse;
    if (this.companyName) {
      name += ` (${this.companyName})`;
    }

    return name;
  }

  get latLng(): Readonly<GeoCoord | undefined> {
    return this.state?.location;
  }

  /**
   * Constructor for PersonEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a PersonEntity from a Person type value.
   *
   * @param person The person type value.
   */
  static from(person: Person): PersonEntity {
    return plainToInstance(PersonEntity, person, {
      excludeExtraneousValues: true
    });
  }

  loadTypeValue(): Promise<Person> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(PersonValidator, object);
  }

  asTypeValue(): Person {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(PersonValidator, object);
  }

  get name(): string {
    return this.firstName + " " + this.lastName;
  }

  get nameReverse(): string {
    return this.lastName + ", " + this.firstName;
  }
}
